import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Check, X, AlertCircle, Minus, Loader2, Search, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Project } from '@/types/project';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Folder column mapping (from folder-config.ts)
const FOLDER_CONFIG = [
  { type: 'root', column: 'new_project_folder', label: 'Root' },
  { type: 'specs', column: 'new_specs_folder', label: 'Specs' },
  { type: 'shop', column: 'new_shop_folder', label: 'Shop' },
  { type: 'po', column: 'new_po_folder', label: 'PO' },
  { type: 'doc', column: 'new_doc_folder', label: 'Doc' },
  { type: 'cost', column: 'new_cost_folder', label: 'Cost' },
  { type: 'build', column: 'new_build_folder', label: 'Build' },
  { type: 'tech', column: 'new_tech_folder', label: 'Tech' },
  { type: 'req', column: 'new_req_folder', label: 'Req' },
  { type: 'proposal', column: 'new_proposal_folder', label: 'Proposal' },
  { type: 'report', column: 'new_report_folder', label: 'Report' },
  { type: 'contract', column: 'new_contract_folder', label: 'Contract' },
] as const;

type FolderStatus = 'exists' | 'deleted' | 'no_permission' | 'error' | 'not_set' | 'unchecked' | 'unlinked' | 'not_found';

interface FolderCheckResult {
  type: string;
  column: string;
  folderId: string | null;
  status: FolderStatus;
  folderName?: string;
  errorMessage?: string;
  foundFolderId?: string; // For unlinked folders found in parent
}

interface CheckResponse {
  success: boolean;
  results?: FolderCheckResult[];
  summary?: {
    total: number;
    withId: number;
    exists: number;
    deleted: number;
    noPermission: number;
    error: number;
    notSet: number;
    unlinked: number;
    notFound: number;
  };
  error?: string;
}

interface Props {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

export function ClearNewGDriveFoldersDialog({ project, isOpen, onClose }: Props) {
  const queryClient = useQueryClient();
  const [isChecking, setIsChecking] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [checkResults, setCheckResults] = useState<FolderCheckResult[] | null>(null);
  const [summary, setSummary] = useState<CheckResponse['summary'] | null>(null);

  // Get current folder IDs from project
  const projectWithFolders = project as any;
  const currentFolders = FOLDER_CONFIG.map(config => ({
    ...config,
    folderId: projectWithFolders[config.column] as string | null,
  }));

  const foldersWithIds = currentFolders.filter(f => f.folderId);

  // Check folders via Edge Function
  const handleCheck = async () => {
    setIsChecking(true);
    setCheckResults(null);
    setSummary(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      const { data, error } = await supabase.functions.invoke<CheckResponse>('check-gdrive-folders', {
        body: { projectId: project.id },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Unknown error');
      }

      setCheckResults(data.results || []);
      setSummary(data.summary || null);
      toast.success('Đã kiểm tra xong');
    } catch (error) {
      console.error('Check folders error:', error);
      toast.error(`Lỗi kiểm tra: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsChecking(false);
    }
  };

  // Clear all folder IDs from database
  const handleClear = async () => {
    setIsClearing(true);

    try {
      // Build update object with all folder columns set to null
      const updateData: Record<string, null> = {};
      FOLDER_CONFIG.forEach(config => {
        updateData[config.column] = null;
      });

      const { error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', project.id);

      if (error) {
        throw error;
      }

      // Invalidate queries to refresh UI
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });

      toast.success(`Đã xóa ${foldersWithIds.length} folder IDs khỏi database`);
      setShowConfirm(false);
      onClose();
    } catch (error) {
      console.error('Clear folders error:', error);
      toast.error(`Lỗi xóa: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsClearing(false);
    }
  };

  // Get status icon and color
  const getStatusDisplay = (status: FolderStatus) => {
    switch (status) {
      case 'exists':
        return { icon: <Check className="h-4 w-4" />, color: 'text-green-500', label: 'Còn tồn tại' };
      case 'deleted':
        return { icon: <X className="h-4 w-4" />, color: 'text-red-500', label: 'Đã bị xóa' };
      case 'no_permission':
        return { icon: <AlertCircle className="h-4 w-4" />, color: 'text-yellow-500', label: 'Không quyền' };
      case 'error':
        return { icon: <AlertTriangle className="h-4 w-4" />, color: 'text-orange-500', label: 'Lỗi' };
      case 'not_set':
        return { icon: <Minus className="h-4 w-4" />, color: 'text-muted-foreground', label: 'Chưa set' };
      case 'unlinked':
        return { icon: <AlertCircle className="h-4 w-4" />, color: 'text-blue-500', label: 'Tồn tại (chưa link)' };
      case 'not_found':
        return { icon: <X className="h-4 w-4" />, color: 'text-red-500', label: 'Không tồn tại' };
      default:
        return { icon: <Minus className="h-4 w-4" />, color: 'text-muted-foreground', label: 'Chưa kiểm tra' };
    }
  };

  // Get result for a folder type
  const getResultForType = (type: string): FolderCheckResult | undefined => {
    return checkResults?.find(r => r.type === type);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Kiểm tra & Xóa NEW GDrive Folders
            </DialogTitle>
            <DialogDescription>
              Kiểm tra trạng thái folders trên Google Drive trước khi xóa IDs khỏi database.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-600 dark:text-yellow-400">Lưu ý quan trọng</p>
                <p className="text-muted-foreground">
                  Chỉ xóa folder IDs trong database. Folders trên Google Drive sẽ <strong>KHÔNG</strong> bị xóa.
                </p>
              </div>
            </div>

            {/* Check button */}
            <Button 
              onClick={handleCheck} 
              disabled={isChecking || foldersWithIds.length === 0}
              variant="outline"
              className="w-full"
            >
              {isChecking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Đang kiểm tra...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Kiểm tra Google Drive
                </>
              )}
            </Button>

            {/* Folder list */}
            <ScrollArea className="h-[280px] border rounded-lg">
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-[100px_1fr_120px] gap-2 text-xs font-medium text-muted-foreground border-b pb-2 mb-2">
                  <span>Folder</span>
                  <span>ID</span>
                  <span>Status</span>
                </div>
                {currentFolders.map(folder => {
                  const result = getResultForType(folder.type);
                  const status = result?.status || (folder.folderId ? 'unchecked' : 'not_set');
                  const statusDisplay = getStatusDisplay(status);

                  return (
                    <div 
                      key={folder.type}
                      className="grid grid-cols-[100px_1fr_120px] gap-2 items-center py-1.5 text-sm"
                    >
                      <span className="font-medium">{folder.label}</span>
                      <span className="font-mono text-xs text-muted-foreground truncate">
                        {folder.folderId ? folder.folderId.substring(0, 12) + '...' : '(chưa có)'}
                      </span>
                      <span className={`flex items-center gap-1.5 ${statusDisplay.color}`}>
                        {statusDisplay.icon}
                        <span className="text-xs">{statusDisplay.label}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Summary */}
            {summary && (
              <div className="flex flex-wrap gap-3 text-sm p-3 bg-muted rounded-lg">
                <span className="text-muted-foreground">Tổng: <strong>{summary.withId}</strong> có ID</span>
                {summary.exists > 0 && (
                  <span className="text-green-600">✓ {summary.exists} còn tồn tại</span>
                )}
                {summary.deleted > 0 && (
                  <span className="text-red-600">✗ {summary.deleted} đã xóa</span>
                )}
                {summary.noPermission > 0 && (
                  <span className="text-yellow-600">⚠ {summary.noPermission} không quyền</span>
                )}
                {summary.unlinked && summary.unlinked > 0 && (
                  <span className="text-blue-600">🔗 {summary.unlinked} chưa link</span>
                )}
                {summary.notFound && summary.notFound > 0 && (
                  <span className="text-red-600">✗ {summary.notFound} không tồn tại</span>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              Đóng
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowConfirm(true)}
              disabled={foldersWithIds.length === 0 || isClearing}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Xóa {foldersWithIds.length} Folder IDs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa Folder IDs?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn sắp xóa <strong>{foldersWithIds.length}</strong> folder IDs khỏi database cho project <strong>{project.project_name}</strong>.
              <br /><br />
              Hành động này sẽ:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Xóa tất cả <code>new_*_folder</code> IDs trong database</li>
                <li><strong>KHÔNG</strong> xóa folders trên Google Drive</li>
                <li>Cho phép tạo lại folders mới sau này</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              disabled={isClearing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Đang xóa...
                </>
              ) : (
                'Xác nhận xóa'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
